/**
 * useResourceUpdate — PUT partial fields, invalidate list + one-cache.
 *
 * Contract: PUT /api/<resource>/<id> body → 200 + updated row
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/lib/api";

export function useResourceUpdate(resource: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string | number; data: Record<string, unknown> }): Promise<unknown> => {
      const res = await api(
        `/api/${encodeURIComponent(resource)}/${encodeURIComponent(String(id))}`,
        { method: "PUT", body: JSON.stringify(data) },
      );
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`PUT /api/${resource}/${id} → ${res.status}${text ? `: ${text}` : ""}`);
      }
      return res.json();
    },
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: [resource, "list"] });
      qc.removeQueries({ queryKey: [resource, "one", id] });
    },
  });
}
